// deadlineKinds.js — refactor 2026-05-21.
//
// Khai báo các loại deadline đề tài SV + Cơ sở để dùng chung giữa:
//   - Filter "Sắp đến hạn / Quá hạn" trong StudentProjectTable + InstitutionalProjectTable.
//   - (Tham chiếu) label kind trong NotificationRules admin page.
//
// Mỗi kind có:
//   - key:     id ổn định (match `notification_rules.kind` BE — không bắt buộc).
//   - label:   text hiển thị cho user (Vietnamese).
//   - source:  hint nguồn data (`endTime` = trên row | `yearlyDue.<field>` = từ
//              YearlyDueDate config). Helper computeDeadlines.js sẽ dispatch.
//   - applies: optional (row) => boolean — kind có áp dụng cho row này không?
//              Vd BCTĐ Cơ sở chỉ áp dụng đề tài 2 năm (duration_months=24).
//
// Filter & badge tooltip đọc cùng metadata này → đổi label 1 chỗ là cả 2 nơi
// đồng bộ. Loose coupling với NotificationRule (không runtime fetch rule).

// Các status END (không còn deadline ý nghĩa) cho SR và IR.
// Row ở các status này → cell "Sắp hạn" hiển thị "—".
const SR_END_STATUSES = new Set([
  "registration_rejected",
  "conducted_rejected",
  "stop_accepted",
  "progress_rejected",
  "acceptance_accepted",
  "acceptance_rejected",
]);

const IR_END_STATUSES = new Set([
  "mod_rejected",
  "admin_rejected",
  "selection_council_rejected",
  "midterm_council_rejected",
  "final_council_rejected",
  "final_council_accepted",
  "stop_accepted",
]);

export function isSRRowDone(row) {
  return SR_END_STATUSES.has(_lastStatus(row));
}
export function isIRRowDone(row) {
  return IR_END_STATUSES.has(_lastStatus(row));
}

// Lấy status chuỗi cuối (row.status có thể là array sau khi rowRender wrap).
function _lastStatus(row) {
  if (!row) return "";
  const s = row.rawStatus || row.status;
  if (Array.isArray(s) && s.length > 0) {
    const last = s[s.length - 1];
    return typeof last === "object" ? last?.value || "" : last || "";
  }
  if (typeof s === "string") {
    const arr = s.split(",");
    return arr[arr.length - 1] || "";
  }
  return "";
}

export const DEADLINE_KINDS_SR = [
  {
    key: "sr_end_time",
    label: "Kết thúc đề tài",
    short: "Kết thúc",
    source: "endTime",
    applies: (row) => !isSRRowDone(row) && !!row.endTime,
  },
  {
    // Refactor 2026-08-26: bỏ BCTĐ đợt 2 — chỉ còn 1 mốc báo cáo tiến độ.
    key: "sr_bctd_1",
    label: "Báo cáo tiến độ",
    short: "BCTĐ",
    source: "yearlyDue.report1EndTime",
    // Áp dụng khi đề tài đang ở giai đoạn conducted/progress và BCTĐ chưa được
    // Trưởng khoa duyệt đạt.
    applies: (row) => {
      if (isSRRowDone(row)) return false;
      const last = _lastStatus(row);
      const reports = row.progressReports || [];
      const report = reports.find((r) => r.iteration === 1);
      if (report?.status === "accepted") return false;
      return [
        "conducted",
        "conducted_adjust_request",
        "progress_draft",
        "progress_submitted",
        "progress_adjust_request",
      ].includes(last);
    },
  },
  {
    key: "sr_acceptance",
    label: "Nghiệm thu",
    short: "Nghiệm thu",
    source: "yearlyDue.acceptEndTime",
    applies: (row) => {
      if (isSRRowDone(row)) return false;
      const last = _lastStatus(row);
      // Hạn nộp BC tổng kết — apply từ progress_accepted trở đi tới khi đã
      // nộp acceptance_submitted (sau đó là việc của HĐ, hết deadline CN).
      return [
        "conducted",
        "progress_accepted",
        "acceptance_ready",
        "acceptance_adjust_request",
      ].includes(last);
    },
  },
];

export const DEADLINE_KINDS_IR = [
  {
    key: "ir_end_time",
    label: "Kết thúc đề tài",
    short: "Kết thúc",
    source: "endTime",
    applies: (row) => !isIRRowDone(row) && !!row.endTime,
  },
  {
    key: "ir_bctd",
    label: "Báo cáo tiến độ giữa kỳ (2y)",
    short: "BCTĐ",
    source: "yearlyDue.report1EndTime",
    applies: (row) => {
      if (isIRRowDone(row)) return false;
      // BCTĐ Cơ sở chỉ áp dụng đề tài 2y (24 tháng), giai đoạn conducted/midterm.
      if (row.durationMonths !== 24) return false;
      const last = _lastStatus(row);
      const pr = row.progressReport;
      if (pr?.status === "accepted") return false;
      return [
        "conducted",
        "midterm_report_submitted",
        "midterm_council_pending",
        "midterm_council_adjust_request",
      ].includes(last);
    },
  },
  {
    key: "ir_acceptance",
    label: "Nộp báo cáo tổng kết",
    short: "Tổng kết",
    source: "yearlyDue.acceptEndTime",
    applies: (row) => {
      if (isIRRowDone(row)) return false;
      const last = _lastStatus(row);
      return [
        "conducted",
        "conducted_phase2",
        "final_report_submitted",
        "final_council_pending",
        "final_council_adjust_request",
      ].includes(last);
    },
  },
];

// Urgency bucket cho 1 deadline.
//   -∞  → daysLeft < 0  → "overdue" 🔴
//   0..7                  → "soon"    🟡
//   8..30                 → "near"    🟢 (lighter)
//   >30                   → "ok"      🟢
//   null (no date)        → "none"    ⚪
export function bucketOfDaysLeft(daysLeft) {
  if (daysLeft === null || daysLeft === undefined || Number.isNaN(daysLeft)) return "none";
  if (daysLeft < 0) return "overdue";
  if (daysLeft <= 7) return "soon";
  if (daysLeft <= 30) return "near";
  return "ok";
}

// Thứ tự ưu tiên (worst first) để pick "worst case" cho cell badge.
export const BUCKET_ORDER = ["overdue", "soon", "near", "ok", "none"];

export const BUCKET_META = {
  overdue: { color: "#e00000", bg: "#fdecec", icon: "🚨", label: "Quá hạn" },
  soon:    { color: "#b58105", bg: "#fff8e1", icon: "⏰", label: "Sắp đến hạn" },
  near:    { color: "#2e7d32", bg: "#e8f5e9", icon: "📅", label: "Còn hạn" },
  ok:      { color: "#558b2f", bg: "#f1f8e9", icon: "📅", label: "Còn hạn" },
  none:    { color: "#9e9e9e", bg: "#f5f5f5", icon: "⚪", label: "—" },
};
